import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Typography, Paper, Radio, RadioGroup, FormControlLabel, Divider, IconButton, Chip, TextField as MUITextField } from '@material-ui/core';
import { Button, TextField, NumberField, SmSpace, MdSpace, AutoComplete } from 'm2m.components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { DistribuirLoteRequest, EmissaoStatus, InstrumentoFinanceiroApiResponse, CoordenadorResponse, RegistroStatus, AtualizarCoordenadoresRequest, ParticipanteResponse, FuncaoParticipante } from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import { stripCpfCnpjFormatting } from 'utils/validation';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import moment from 'moment';
import { addAlert } from 'store/modules/snackBar';
import CoordenadoresConfirmationDialog from 'components/instrumentos/registro/caracteristicas/coordenadoresConfirmationDialog';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

interface CoordenadorComLimite extends CoordenadorResponse {
    limite: number;
}

const DelimitacaoEmLote: React.FC = () => {
    const dispatch = useDispatch();
    const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentos);
    const getInstrumentoApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhes);
    const putDistribuirLote = useSelector((state: RootState) => state.coordenadoresApi.coordenadores.putDistribuirLote);
    const putAtualizarCoordenadoresCompleto = useSelector(
        (state: RootState) => state.coordenadoresApi.coordenadores.putAtualizarCoordenadoresCompleto
    );
    const usuario = useSelector((state: RootState) => state.auth.usuario);
    const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
    const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
    const [valorRadio, setValorRadio] = useState<string>('');
    const [limiteErrors, setLimiteErrors] = useState<{ [key: number]: string }>({});
    const [coordenadoresOriginais, setCoordenadoresOriginais] = useState<Array<CoordenadorResponse>>([]);
    const [coordenadoresComLimite, setCoordenadoresComLimite] = useState<Array<CoordenadorComLimite>>([]);
    const [totalDistribuido, setTotalDistribuido] = useState<number>(0);
    

    const [participantes, setParticipantes] = useState<Array<ParticipanteResponse>>([]);
    const [selectedParticipante, setSelectedParticipante] = useState<ParticipanteResponse | null>(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
    const [pendingCoordenadoresToAdd, setPendingCoordenadoresToAdd] = useState<ParticipanteResponse | null>(null);
    const [pendingCoordenadoresToRemove, setPendingCoordenadoresToRemove] = useState<CoordenadorComLimite | null>(null);
    const [hasDelimitacao, setHasDelimitacao] = useState<boolean>(false);

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValorRadio(event.target.value);
        setCoordenadoresComLimite(prev =>
            prev.map(coord => ({ ...coord, limite: 0 }))
        );
        setTotalDistribuido(0);
    };

    const totalDisponivel = useMemo(() => {
        if (!instrumento) return 0;

        if (valorRadio === 'VALOR') {
            return instrumento.criteriosRemuneracao?.volumeEmissao || 0;
        } else {
            return instrumento.criteriosRemuneracao?.quantidadeEmitida || 0;
        }
    }, [instrumento, valorRadio]);

    const saldoRestante = useMemo(() => {
        return totalDisponivel - totalDistribuido;
    }, [totalDisponivel, totalDistribuido]);

    const getLimiteHelperText = (index: number) => {
        const coord = coordenadoresComLimite[index];
        if (!coord) return '';

        if (valorRadio === 'VALOR') {
            return `Valor máximo disponível: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(saldoRestante + (coord.limite || 0))}`;
        } else {
            return `Quantidade máxima disponível: ${new Intl.NumberFormat('pt-BR').format(saldoRestante + (coord.limite || 0))}`;
        }
    };

    const handleLimiteChange = (index: number, value: number | null | undefined) => {
        if (value === undefined || value === null) {
            const novosCoordenadores = [...coordenadoresComLimite];
            novosCoordenadores[index].limite = 0;

            setLimiteErrors(prev => ({ ...prev, [index]: '' }));

            const novoTotal = novosCoordenadores.reduce((acc, curr) => acc + (curr.limite || 0), 0);
            setCoordenadoresComLimite(novosCoordenadores);
            setTotalDistribuido(novoTotal);
            return;
        }

        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        const otherLimites = coordenadoresComLimite.reduce((acc, curr, idx) =>
            idx !== index ? acc + (curr.limite || 0) : acc, 0);
        const maxPossible = totalDisponivel - otherLimites;

        if (numericValue > maxPossible) {
            setLimiteErrors(prev => ({
                ...prev,
                [index]: valorRadio === 'VALOR' ?
                    `Valor excede o máximo disponível` :
                    `Quantidade excede o máximo disponível`
            }));
            return;
        }

        setLimiteErrors(prev => ({ ...prev, [index]: '' }));
        const novosCoordenadores = [...coordenadoresComLimite];
        novosCoordenadores[index].limite = numericValue;
        const novoTotal = novosCoordenadores.reduce((acc, curr) => acc + (curr.limite || 0), 0);
        setCoordenadoresComLimite(novosCoordenadores);
        setTotalDistribuido(novoTotal);
    };


    const updateCoordenadoresCompleto = async (
        codigo: string,
        coordenadoresIds: string[],
        skipAlerts: boolean = false
    ) => {
        try {
            if (!codigo) {
                console.error('codigo is required');
                return;
            }

            const request: AtualizarCoordenadoresRequest = {
                coordenadores: coordenadoresIds.map(id => ({ coordenadorId: id })),
                coordenadorLiderId: usuario?.participante.id
            };

            const result = await putAtualizarCoordenadoresCompleto(
                dispatch,
                codigo,
                request
            );
            
            if (!skipAlerts) {
                if (result && result.mensagem) {
                    dispatch(addAlert({ message: result.mensagem, severity: 'warning' }));
                } else {
                    dispatch(addAlert({ message: 'Coordenadores atualizados com sucesso', severity: 'success' }));
                }
            }
            
            return result;
        } catch (error: any) {
            console.error('Error updating coordinators:', error);
            
            if (!skipAlerts) {
                let errorMessage = '';
                
                if (error?.response?.data?.mensagem) {
                    errorMessage = error.response.data.mensagem;
                } else if (error?.mensagem) {
                    errorMessage = error.mensagem;
                } else if (error?.message && !error.message.includes('status code')) {
                    errorMessage = error.message;
                }
                
                const detailedMessage = errorMessage
                    ? `Erro ao atualizar coordenadores: ${errorMessage}`
                    : 'Erro ao atualizar coordenadores';
                    
                dispatch(addAlert({ message: detailedMessage, severity: 'error' }));
            }
            
            throw error;
        }
    };

    const handleAddCoordenador = (coordenador: ParticipanteResponse) => {

        const exists = coordenadoresComLimite.some(c => c.id === coordenador.id);
        if (exists) {
            dispatch(addAlert({ severity: 'warning', message: 'Este coordenador já está na lista.' }));
            return;
        }

        if (hasDelimitacao) {
            setPendingCoordenadoresToAdd(coordenador);
            setShowConfirmationDialog(true);
        } else {
            const newCoordenador: CoordenadorComLimite = {
                id: coordenador.id,
                cnpj: coordenador.cnpj,
                razaoSocial: coordenador.razaoSocial,
                participante: coordenador,
                valor: 0,
                limite: 0,
                tipoDelimitacao: valorRadio === 'VALOR' ? 'VALOR' : 'QUANTIDADE',
                instrumentoFinanceiroId: instrumento?.id || 0
            };

            const updatedCoordenadores = [...coordenadoresComLimite, newCoordenador];
            setCoordenadoresComLimite(updatedCoordenadores);
            

            if (instrumento?.codigo) {
                updateCoordenadoresCompleto(
                    instrumento.codigo,
                    updatedCoordenadores.map(c => c.id),
                    false
                ).catch(error => {
                    console.error('Error adding coordinator:', error);
                    setCoordenadoresComLimite([...coordenadoresOriginais] as CoordenadorComLimite[]);
                });
            } else {
                dispatch(addAlert({ message: 'Erro: Instrumento não encontrado', severity: 'error' }));
            }
        }
    };

    const handleRemoveCoordenador = (coordenador: CoordenadorComLimite) => {
        if (usuario?.participante?.id === coordenador?.participante?.id) {
            dispatch(addAlert({ message: 'Você não pode remover a si mesmo da lista de coordenadores', severity: 'error' }));
            return;
        }

        if (hasDelimitacao || coordenador.limite > 0) {
            setPendingCoordenadoresToRemove(coordenador);
            setShowConfirmationDialog(true);
        } else {
            const updatedCoordenadores = coordenadoresComLimite.filter(c => c.id !== coordenador.id);
            setCoordenadoresComLimite(updatedCoordenadores);
            
            if (instrumento?.codigo) {
                updateCoordenadoresCompleto(
                    instrumento.codigo,
                    updatedCoordenadores.map(c => c.id),
                    false
                ).catch(error => {
                    console.error('Error removing coordinator:', error);
                    setCoordenadoresComLimite([...coordenadoresOriginais] as CoordenadorComLimite[]);
                });
            } else {
                dispatch(addAlert({ message: 'Erro: Instrumento não encontrado', severity: 'error' }));
            }
        }
    };

    const handleConfirmDialogClose = () => {
        setShowConfirmationDialog(false);
        setPendingCoordenadoresToAdd(null);
        setPendingCoordenadoresToRemove(null);
    };

    const handleConfirmDialogConfirm = async () => {
        setIsConfirmLoading(true);
        
        try {

            const originalCoordenadores = [...coordenadoresComLimite];
            
            if (instrumento?.codigo) {

                if (pendingCoordenadoresToAdd) {
                    const newCoordenador: CoordenadorComLimite = {
                        id: pendingCoordenadoresToAdd.id,
                        cnpj: pendingCoordenadoresToAdd.cnpj,
                        razaoSocial: pendingCoordenadoresToAdd.razaoSocial,
                        participante: pendingCoordenadoresToAdd,
                        valor: 0,
                        limite: 0,
                        tipoDelimitacao: valorRadio === 'VALOR' ? 'VALOR' : 'QUANTIDADE',
                        instrumentoFinanceiroId: instrumento?.id || 0
                    };
                    
        
                    const updatedCoordenadores = [...coordenadoresComLimite, newCoordenador];
                    setCoordenadoresComLimite(updatedCoordenadores);
                    

                    await updateCoordenadoresCompleto(
                        instrumento.codigo,
                        updatedCoordenadores.map(c => c.id),
                        true
                    );
                    

                    const resetCoordenadores = updatedCoordenadores.map(c => ({ ...c, limite: 0 }));
                    setCoordenadoresComLimite(resetCoordenadores);
                    setTotalDistribuido(0);
                    setHasDelimitacao(false);
                    
                    dispatch(addAlert({ message: 'Coordenador adicionado com sucesso. As delimitações foram resetadas.', severity: 'success' }));
                }
                
            
                if (pendingCoordenadoresToRemove) {
        
                    const updatedCoordenadores = coordenadoresComLimite.filter(c => c.id !== pendingCoordenadoresToRemove.id);
                    setCoordenadoresComLimite(updatedCoordenadores);
                    

                    await updateCoordenadoresCompleto(
                        instrumento.codigo,
                        updatedCoordenadores.map(c => c.id),
                        true
                    );
                    

                    const resetCoordenadores = updatedCoordenadores.map(c => ({ ...c, limite: 0 }));
                    setCoordenadoresComLimite(resetCoordenadores);
                    setTotalDistribuido(0);
                    setHasDelimitacao(false);
                    
                    dispatch(addAlert({ message: 'Coordenador removido com sucesso. As delimitações foram resetadas.', severity: 'success' }));
                }
                

                setShowConfirmationDialog(false);
                setPendingCoordenadoresToAdd(null);
                setPendingCoordenadoresToRemove(null);
            }
        } catch (error: any) {

            

            setCoordenadoresComLimite([...coordenadoresOriginais] as CoordenadorComLimite[]);
            

            let errorMessage = '';
            
            if (error?.response?.data?.mensagem) {
                errorMessage = error.response.data.mensagem;
            } else if (error?.mensagem) {
                errorMessage = error.mensagem;
            } else if (error?.message && !error.message.includes('status code')) {
                errorMessage = error.message;
            }
            

            const detailedMessage = errorMessage
                ? `Erro ao atualizar coordenadores: ${errorMessage}. As alterações foram revertidas.`
                : 'Erro ao atualizar coordenadores. As alterações foram revertidas.';
                
            dispatch(addAlert({ message: detailedMessage, severity: 'error' }));
            

            setShowConfirmationDialog(false);
            setPendingCoordenadoresToAdd(null);
            setPendingCoordenadoresToRemove(null);
        } finally {
            setIsConfirmLoading(false);
        }
    };

    const handleSubmit = async () => {
        const distribuirLoteRequest: DistribuirLoteRequest = {
            coordenadores: coordenadoresComLimite.map(coord => ({
                coordenadorId: coord.id,
                limite: coord.limite,
                cnpj: coord.cnpj,
                razaoSocial: coord.razaoSocial
            })),
            tipoDelimitacao: valorRadio === 'VALOR' ? 'VALOR' : 'QUANTIDADE'
        };

        try {
            await putDistribuirLote(
                dispatch,
                instrumento?.instrumentoFinanceiroGuid || '',
                distribuirLoteRequest
            );

            dispatch(addAlert({ severity: 'success', message: 'Delimitação de lotes realizada com sucesso!' }));
            setHasDelimitacao(true);
        } catch (error) {
            dispatch(addAlert({ severity: 'error', message: 'Erro ao enviar os delimitações da operação.' }));
        }
    }


    const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantes);
    
    useEffect(() => {
        const fetchInstrumentos = async () => {
            const result = await getInstrumentosApi(
                dispatch,
                {
                    emissaoStatus: [],
                    registroStatus: [RegistroStatus.Aprovado],
                    coordenadorId: usuario?.participante.id
                }
                , true);
            setInstrumentos(result);
        };
        fetchInstrumentos();
    }, [dispatch, getInstrumentosApi, usuario?.participante.id]);
    

    useEffect(() => {
        const fetchParticipantes = async () => {
            try {
                const result = await getParticipantesApi(
                    dispatch,
                    {
                        funcoes: [FuncaoParticipante.Coordenador],
                        status: RegistroStatus.Aprovado,
                        suspenso: false
                    },
                    true
                );
                setParticipantes(result || []);
            } catch (error) {
                console.error('Error fetching participantes:', error);
            }
        };
        fetchParticipantes();
    }, [dispatch, getParticipantesApi]);

    return (
        <div style={{ marginRight: 40, marginBottom: 40 }}>
            <Typography variant="h2">{'Delimitação de Lotes'}</Typography>
            <MdSpace />
            

            <CoordenadoresConfirmationDialog
                open={showConfirmationDialog}
                onClose={handleConfirmDialogClose}
                onConfirm={handleConfirmDialogConfirm}
                isLoading={isConfirmLoading}
            />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper style={{ padding: 24 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <InstrumentoAutocomplete
                                    instrumentos={instrumentos}
                                    onChange={async (value) => {
                                        if (!value?.instrumentoFinanceiroGuid) return;

                                        try {
                                            const instrumentoDetalhes = await getInstrumentoApi(
                                                dispatch,
                                                value.instrumentoFinanceiroGuid,
                                                true
                                            );

                                            setInstrumento(instrumentoDetalhes || value);

                                            const coordenadoresList = instrumentoDetalhes?.coordenadores || value.coordenadores || [];
                                            setCoordenadoresOriginais(coordenadoresList);

                                            const coordenadoresFiltrados = coordenadoresList
                                                .map((coord) => {
                                                    const limitePreDefinido = coord.valor !== undefined ? coord.valor : 0;
                                                    return { 
                                                        ...coord, 
                                                        limite: limitePreDefinido
                                                    };
                                                });
                                            
                                            setCoordenadoresComLimite(coordenadoresFiltrados);
                                            
                                            const novoTotalDistribuido = coordenadoresFiltrados.reduce(
                                                (acc, curr) => acc + (curr.limite || 0), 0
                                            );


                                            const hasAnyLimit = coordenadoresFiltrados.some(c => c.limite > 0);
                                            setHasDelimitacao(hasAnyLimit);
                                            
                                            if (coordenadoresFiltrados.length > 0 && coordenadoresFiltrados[0].tipoDelimitacao) {
                                                setValorRadio(coordenadoresFiltrados[0].tipoDelimitacao);
                                            } else {
                                                setValorRadio('VALOR');
                                            }
                                            setTotalDistribuido(novoTotalDistribuido);
                                        } catch (error) {
                                            console.error('Error fetching instrument details:', error);
                                        }
                                    }}
                                    instrumento={instrumento} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Razão social do Emissor"
                                    value={instrumento?.emissor?.razaoSocial || ''}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CNPJ do Emissor"
                                    fullWidth
                                    value={addCpfCnpjMask(instrumento?.emissor?.cnpj || '')}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    disabled
                                    label="Data de Emissão"
                                    fullWidth
                                    value={instrumento?.criteriosRemuneracao?.dataEmissao ? moment(instrumento.criteriosRemuneracao.dataEmissao).format('DD/MM/YYYY') : ''}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <NumberField
                                    label="Valor Emissão"
                                    value={instrumento?.criteriosRemuneracao?.volumeEmissao || 0}
                                    onChange={(_) => _}
                                    numberType='eightDecimals'
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <NumberField
                                    label="PU"
                                    value={instrumento?.criteriosRemuneracao?.valorNominalUnitarioEmissao || 0}
                                    onChange={(_) => _}
                                    numberType='eightDecimals'
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper style={{ padding: 24 }}>
                        <Typography variant="body1">Delimitar Lotes por:</Typography>
                        <SmSpace />

                        <RadioGroup
                            row
                            value={valorRadio}
                            onChange={handleRadioChange}
                        >
                            <FormControlLabel value="VALOR" control={<Radio color="primary" />} label="Valor" />
                            <FormControlLabel value="QUANTIDADE" control={<Radio color="primary" />} label="Quantidade" />
                        </RadioGroup>

                        <Grid item xs={12}>
                            <Divider style={{ margin: '16px 0' }} />
                        </Grid>


                        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                            <Grid item xs={8}>
                                <AutoComplete
                                    options={participantes.filter(p => 
                                        !coordenadoresComLimite.some(c => c.id === p.id)
                                    )}
                                    getOptionLabel={(option: ParticipanteResponse) => addCpfCnpjMask(option.cnpj)}
                                    filterOptions={(options: Array<ParticipanteResponse>, state) =>
                                        options.filter((_) => stripCpfCnpjFormatting(_.cnpj).includes(stripCpfCnpjFormatting(state.inputValue)))
                                    }
                                    onChange={(value) => setSelectedParticipante(value)}
                                    filterSelectedOptions
                                    label="Adicionar Coordenador"
                                    placeholder={'Pesquisar...'}
                                    renderOption={(option: ParticipanteResponse) => (
                                        <div style={{ width: '100%' }}>
                                            <div style={{ display: 'flex' }}>
                                                <Typography style={{ margin: 'auto 12px' }}>{addCpfCnpjMask(option.cnpj)}</Typography>
                                            </div>
                                            <div>
                                                <Typography style={{ margin: 'auto 12px' }} variant="body2">
                                                    {option.razaoSocial}
                                                </Typography>
                                            </div>
                                        </div>
                                    )}
                                    renderInput={(params) => (
                                        <MUITextField
                                            {...params}
                                            label="Adicionar Coordenador"
                                            InputProps={{ ...params.InputProps }}
                                            variant="outlined"
                                            placeholder={'Pesquisar...'}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => selectedParticipante && handleAddCoordenador(selectedParticipante)}
                                    disabled={!selectedParticipante}
                                    style={{ marginTop: '8px' }}
                                >
                                    Adicionar
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <Typography variant="body1">Coordenador</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="body1">CNPJ</Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <Typography variant="body1">Limite</Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="body1">Ações</Typography>
                            </Grid>

                            <SmSpace />

                            {coordenadoresComLimite.map((coord, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={3}>
                                        <Typography variant="body2">{coord.razaoSocial}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="body2">{addCpfCnpjMask(coord.cnpj)}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <NumberField
                                            label="Limite"
                                            value={coord.limite}
                                            onChange={(value) => handleLimiteChange(index, value)}
                                            numberType={valorRadio === 'VALOR' ? 'money' : 'integer'}
                                            error={!!limiteErrors[index]}
                                            errorMessage={limiteErrors[index]}
                                            helperText={getLimiteHelperText(index)}
                                        />
                                    </Grid>
                                    <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', height: '100%' }}>
                                        <IconButton 
                                            onClick={() => handleRemoveCoordenador(coord)}
                                            style={{ 
                                                backgroundColor: '#d32f2f', 
                                                padding: '8px', 
                                                border: '1px solid #b71c1c',
                                                color: 'white'
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                    <SmSpace />
                                </React.Fragment>
                            ))}


                            <Grid item xs={12}>
                                <Divider style={{ margin: '16px 0' }} />
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={11}>
                                    <Typography variant="subtitle1" style={{ textAlign: 'right' }}>
                                        {valorRadio === 'VALOR' ? 'Valor Emissão:' : 'Quantidade Emitida:'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Typography variant="subtitle1">
                                        {valorRadio === 'VALOR'
                                            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(instrumento?.criteriosRemuneracao?.volumeEmissao || 0)
                                            : new Intl.NumberFormat('pt-BR').format(instrumento?.criteriosRemuneracao?.quantidadeEmitida || 0)
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={11}>
                                    <Typography variant="subtitle1" style={{ textAlign: 'right', color: saldoRestante < 0 ? 'red' : 'inherit' }}>
                                        Saldo Restante:
                                    </Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Typography variant="subtitle1" style={{ color: saldoRestante < 0 ? 'red' : 'inherit' }}>
                                        {valorRadio === 'VALOR'
                                            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(saldoRestante)
                                            : new Intl.NumberFormat('pt-BR').format(saldoRestante)
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12} style={{ textAlign: 'right', marginTop: 24 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={saldoRestante !== 0 || !instrumento || Object.keys(limiteErrors).some(key => !!limiteErrors[parseInt(key)])}
                        onClick={handleSubmit}
                    >
                        Confirmar
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default DelimitacaoEmLote;
